import { Users } from 'lucide-react';

import { NoticesView } from '@/components/notices/NoticesView';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { ApiRequestError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import { parentApi } from '../api/parent.api';
import { useParentChild, useSelectedChild } from '../store/child.store';
import { PARENT_PALETTE } from '../theme';

/**
 * Notices, for the parent of the child on screen.
 *
 * Two boards, drawn as one list by the same `NoticesView` the child's own panel
 * uses:
 *
 *   `GET /parent/children/:uuid/notices`   what that child's teachers told the
 *                                          family — their "Parents only" and
 *                                          "Both" notices, never the ones meant
 *                                          for the children alone
 *   `GET /parent/notice-board`             what the office put up for everyone
 *
 * Both are the server's answer about who this family is. Nothing is added on
 * the way through: no venue, no attachment, no sample notices on an empty board.
 *
 * The child is never in the address. It comes from the switcher in the sidebar,
 * and the server resolves which children the caller may see from their own
 * token — an id in the URL is an id somebody can change (CLAUDE.md §10).
 */
export function ParentNoticesPage() {
  const child = useSelectedChild();
  const isLoadingChildren = useParentChild((state) => state.isLoading);
  const childrenError = useParentChild((state) => state.error);
  const reloadChildren = useParentChild((state) => state.load);

  const {
    data,
    isLoading: isLoadingClass,
    error: classError,
    refetch: refetchClass,
  } = useApiQuery(
    () => (child ? parentApi.notices(child.uuid) : Promise.resolve(null)),
    [child?.uuid],
  );

  // The office's board is the same whichever child is on screen, so it is read
  // once rather than again on every switch.
  const {
    data: board,
    isLoading: isLoadingBoard,
    error: boardError,
    refetch: refetchBoard,
  } = useApiQuery(() => parentApi.noticeBoard(), []);

  // Switching children refetches; until the new list lands, the old one belongs
  // to somebody else and must not be drawn under this child's name.
  const classNotices = data && data.child.uuid === child?.uuid ? data : null;

  if (isLoadingChildren) {
    return (
      <div className={cn(PARENT_PALETTE, 'flex flex-col gap-6')}>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 rounded-3xl" />
      </div>
    );
  }

  if (childrenError) {
    return (
      <div className={cn(PARENT_PALETTE)}>
        <QueryError
          title="Could not load your children"
          error={new ApiRequestError(childrenError, 0, 'INTERNAL_ERROR')}
          onRetry={() => void reloadChildren()}
        />
      </div>
    );
  }

  if (!child) {
    return (
      <div className={cn(PARENT_PALETTE, 'card')}>
        <EmptyState
          icon={<Users className="h-5 w-5" />}
          title="No child linked to your account yet"
          description="Your school office links a parent's account to their child's record. Once that is done, the notices sent to you appear here."
        />
      </div>
    );
  }

  return (
    <div className={cn(PARENT_PALETTE, 'flex flex-col gap-5')}>
      <NoticesView
        classNotices={classNotices}
        instituteNotices={board}
        isLoading={isLoadingClass || isLoadingBoard || (!classNotices && !classError)}
        error={classError ?? boardError}
        onRetry={() => {
          void refetchClass();
          void refetchBoard();
        }}
        description={`What ${child.fullName}'s teachers and the school office have sent you, for ${child.className}.`}
        emptyDescription="When your child's teachers or the school office send something to parents, it appears here."
      />
    </div>
  );
}
